import { Link } from 'react-router-dom';

import type { MinistryActivity } from '../lib/buckets';
import { messages } from '@/shared/constants/messages';
import { formatList, formatNumber } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';

interface ActivityBlockProps {
  ranked: readonly MinistryActivity[];
  silent: readonly MinistryActivity[];
}

/** Quantos ministérios a lista mostra antes de virar parede. */
const VISIBLE_ROWS = 6;

/**
 * Quais ministérios do seu alcance moveram, e quais não moveram nada.
 *
 * ⚠️ **Só é renderizado para quem tem `readOwnCoordination`**, e a razão não é
 * hierarquia — é que para líder e auxiliar este bloco MENTIRIA. As duas listas
 * vêm de fontes com recortes diferentes (`my_coordination_ids()` para os nomes,
 * `my_ministry_ids()` para as contagens); para coordenador, overseer e admin os
 * conjuntos coincidem, para líder e auxiliar não: eles recebem os ministérios
 * IRMÃOS e não podem ler projeto nenhum deles, então todo irmão apareceria como
 * "não enviou nada". É a mesma parede de zeros que fez a ADR-012 tirar
 * `/coordenacoes` do menu do líder. O gate está na página, com teste que trava.
 *
 * ⚠️ **"não enviaram projeto", nunca "sem atividade"** — ver
 * `messages.home.silentTitle`. Ministério que só tem rascunho chega aqui zerado,
 * porque a RLS esconde rascunho de quem não tem vínculo direto.
 */
export function ActivityBlock({ ranked, silent }: ActivityBlockProps) {
  const max = Math.max(...ranked.map((entry) => entry.total), 0);
  const visible = ranked.slice(0, VISIBLE_ROWS);
  const hidden = ranked.length - visible.length;

  if (ranked.length === 0 && silent.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhum ministério no seu alcance.</p>;
  }

  return (
    <div className="space-y-4">
      {visible.length > 0 ? (
        <ul className="space-y-2">
          {visible.map((entry) => (
            <li key={entry.ministryId}>
              <Link
                to={`/ministerios/${entry.ministryId}`}
                className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 rounded-md px-1 py-1 transition-colors duration-fast ease-out hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
              >
                <span className="truncate text-sm font-medium group-hover:underline">
                  {entry.name}
                </span>
                <span className="tabular text-sm text-muted-foreground">
                  {formatNumber(entry.total)}
                </span>
                {/*
                  A barra vem DEPOIS do nome e do número, na linha de baixo, e
                  não ao lado: em 375px uma barra concorrendo com o nome deixa
                  os dois ilegíveis, e o número já responde a pergunta sozinho.
                  Ela é `aria-hidden` porque não acrescenta nada ao número.
                */}
                <span
                  aria-hidden="true"
                  className="col-span-2 h-1.5 overflow-hidden rounded-full bg-muted"
                >
                  <span
                    className={cn('block h-full rounded-full bg-primary/70')}
                    style={{ width: `${max > 0 ? (entry.total / max) * 100 : 0}%` }}
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      {hidden > 0 ? (
        <p className="text-xs text-muted-foreground">
          e mais {formatNumber(hidden)} {hidden === 1 ? 'ministério' : 'ministérios'}.
        </p>
      ) : null}

      {silent.length > 0 ? (
        <div className="rounded-md bg-muted/60 p-3">
          <p className="text-sm font-medium">{messages.home.silentTitle(silent.length)}</p>
          {/*
            `formatList` já põe o "e" antes do último item, então concatenar
            " e mais 4" produzia "…Movie e Projeção e mais 4". Com truncamento a
            lista vira vírgulas e o "e mais N" faz o papel do último elo.
          */}
          <p className="mt-1 text-sm text-muted-foreground">
            {silent.length > 5
              ? `${silent
                  .slice(0, 5)
                  .map((entry) => entry.name)
                  .join(', ')} e mais ${formatNumber(silent.length - 5)}.`
              : `${formatList(silent.map((entry) => entry.name))}.`}
          </p>
        </div>
      ) : null}
    </div>
  );
}
